import { computed, inject, Injectable, signal } from '@angular/core';

import { NfDatasourceService } from '../../../shared/services/nf-datasource.service';
import type { XContextSource } from '../../../shared/services/nf-datasource-types';

export interface StoreOption {
  value: string;
  label: string;
  image?: string;
}

/**
 * Holds the store-selection context for tools that operate at store level.
 *
 * The store list is populated by loadStores(source) from a schema-declared XContextSource (a
 * schema's `x-context` entry for 'storeId' carries its own `source: {service, url, mapping}` —
 * see dormammu-store-config) — resolved generically via NfDatasourceService.fetchOptions(), not
 * a per-tool API call. Per-client scoping happens via the `client-id` header
 * (nebulaClientIdInterceptor), not a query param, since a schema's `source.url` is static.
 *
 * Template variable: {{context.storeId}} → selectedStoreId()
 */
@Injectable({ providedIn: 'root' })
export class StoreContextService {
  private readonly datasource = inject(NfDatasourceService);

  private readonly _stores = signal<StoreOption[]>([]);
  private readonly _selectedStoreId = signal('');
  private readonly _loading = signal(false);

  readonly stores = this._stores.asReadonly();
  readonly selectedStoreId = this._selectedStoreId.asReadonly();
  readonly loading = this._loading.asReadonly();

  readonly selectedStore = computed(() =>
    this._stores().find((s) => s.value === this._selectedStoreId()),
  );

  select(storeId: string): void {
    this._selectedStoreId.set(storeId);
  }

  clearSelection(): void {
    this._selectedStoreId.set('');
    this._stores.set([]);
  }

  /** Loads the store list from a schema-declared source (x-context's storeId entry). */
  async loadStores(source: XContextSource): Promise<void> {
    this._loading.set(true);
    try {
      const options = await this.datasource.fetchOptions(source);
      this._stores.set(options);
    } finally {
      this._loading.set(false);
    }
  }
}
